import {createCandidates} from '@apps-common/geocorn-sdk/geom/simplify';
import BackIcon from '@mui/icons-material/ArrowBack';
import ForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Alert,
  Box,
  Card,
  CardHeader,
  Grid,
  IconButton,
  MobileStepper,
  Typography,
} from '@mui/material';
import {pink} from '@mui/material/colors';
import {bool, func, number, object, string} from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import AreaLabel from './area-label.jsx';
import {CompareMap} from './compare-map.jsx';

const ChipAndLabel = ({color, label}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
    }}
  >
    <Box
      sx={{
        marginRight: 0.5,
        width: '1em',
        height: '1em',
        display: 'inline-block',
        backgroundColor: color === 'primary' ? `${color}.light` : pink[300],
        border: 'solid 2px',
        borderColor: color === 'primary' ? `${color}.main` : pink[500],
      }}
    />
    <Typography component="span">
      <b>{label}</b>
    </Typography>
  </Box>
);

ChipAndLabel.propTypes = {
  color: string,
  label: string,
};

const CompareGrid = ({original, proposed}) => {
  const {t} = useTranslation();
  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Grid container sx={{marginBottom: 1}}>
        <Grid item xs={6}>
          <ChipAndLabel
            color="secondary"
            label={t('uploadDialog.labelOriginalArea')}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography>
            <AreaLabel geometry={original} />
          </Typography>
        </Grid>
      </Grid>
      <Grid container item>
        <Grid item xs={6}>
          <ChipAndLabel
            color="primary"
            label={t('uploadDialog.labelProposedArea')}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography>
            <AreaLabel geometry={proposed} />
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

CompareGrid.propTypes = {
  original: object,
  proposed: object,
};

const SimplifyFeature = ({
  originalGeometry,
  onSelectCandidate,
  isComplex,
  isValid,
  maxVertexCount,
}) => {
  const {t} = useTranslation();

  const candidates = useMemo(() => {
    // pass the validity value
    return createCandidates(originalGeometry, isValid);
  }, [originalGeometry, isValid]);

  const [selectedCandidate, setSelectedCandidate] = useState(0);
  const candidate = candidates[selectedCandidate];

  useEffect(() => {
    onSelectCandidate(candidates[selectedCandidate]);
  }, [onSelectCandidate, candidates, selectedCandidate]);

  let alertMessage = '';

  if (isComplex && isValid) {
    alertMessage = t('uploadDialog.warningTooComplex', {
      limit: maxVertexCount,
    });
  } else if (!isValid && !isComplex) {
    alertMessage = t('uploadDialog.warningInvalidGeometry');
  } else if (isComplex && !isValid) {
    alertMessage = t('uploadDialog.warningComplexAndInvalid', {
      limit: maxVertexCount,
    });
  }

  return (
    <React.Fragment>
      {alertMessage && (
        <Alert
          severity="warning"
          sx={{marginBottom: 1}}
          data-qe="invalid-geometry-alt-version-warning"
        >
          {alertMessage}
        </Alert>
      )}
      <Box
        key={candidate.label}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingX: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            title={t('uploadDialog.prevAlternative')}
            disabled={selectedCandidate === 0}
            onClick={() => setSelectedCandidate(selectedCandidate - 1)}
            sx={{
              visibility: selectedCandidate === 0 ? 'hidden' : 'visible',
            }}
          >
            <BackIcon />
          </IconButton>
        </Box>
        <Card
          elevation={15}
          sx={{
            width: 550,
            flexGrow: 0,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CardHeader
            title={t(candidate.label)}
            subheader={t(candidate.description)}
            titleTypographyProps={{variant: 'subtitle1'}}
            subheaderTypographyProps={{
              variant: 'body2',
              color: 'textSecondary',
              minHeight: theme => 14 * theme.typography.body2.lineHeight * 2, //make it two lines by default
            }}
            sx={{paddingY: 1}}
          />
          <Box
            sx={{
              height: 256,
              width: '100%',
            }}
          >
            <CompareMap geometry={originalGeometry} candidate={candidate} />
          </Box>
          <Box
            sx={{
              paddingX: 2,
              paddingY: 1,
            }}
          >
            <CompareGrid
              original={originalGeometry}
              proposed={candidate.geometry}
            />
          </Box>
        </Card>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            title={t('uploadDialog.nextAlternative')}
            disabled={selectedCandidate === candidates.length - 1}
            onClick={() => setSelectedCandidate(selectedCandidate + 1)}
          >
            <ForwardIcon />
          </IconButton>
        </Box>
      </Box>
      <MobileStepper
        steps={candidates.length}
        activeStep={selectedCandidate}
        position="static"
        variant="text"
        sx={{
          justifyContent: 'center',
          paddingBottom: 0,
          fontSize: 14,
          backgroundColor: 'transparent',
        }}
      />
    </React.Fragment>
  );
};

SimplifyFeature.propTypes = {
  originalGeometry: object,
  onSelectCandidate: func,
  isComplex: bool,
  isValid: bool,
  maxVertexCount: number,
};

export default SimplifyFeature;
